import { createFileRoute, Link } from "@tanstack/react-router";
import { motion } from "framer-motion";
import { Truck, Headphones, Star, Users, ShieldCheck, TrendingUp, Award, Sparkles } from "lucide-react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { ScrollingBanner } from "@/components/ScrollingBanner";
import { ProductCard } from "@/components/ProductCard";
import { MobileCategoryBar } from "@/components/MobileCategoryBar";
import { products } from "@/data/products";
import summer2026Bg from "@/assets/summer-2026.jpg";


export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: "angle-shop.com — Vêtements & Sneakers Premium" },
      { name: "description", content: "Boutique premium de vêtements et chaussures de marque. T-shirts, hoodies, sneakers — livraison offerte dès 59€." },
      { property: "og:title", content: "angle-shop.com — Vêtements & Sneakers Premium" },
      { property: "og:description", content: "Boutique premium de vêtements et chaussures de marque. Livraison offerte dès 59€." },
      { property: "og:url", content: "https://shopb.life/" },
      { property: "og:type", content: "website" },
    ],
    links: [
      { rel: "canonical", href: "https://shopb.life/" },
    ],
  }),
  component: HomePage,
});

function HomePage() {
  const featured = products.filter((p) => p.badge === "bestseller").slice(0, 6);

  return (
    <main className="min-h-screen bg-background">
      <Header />
      <MobileCategoryBar />
      <ScrollingBanner />

      {/* Hero */}
      <section className="relative overflow-hidden bg-background">
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 sm:py-32 lg:py-40">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="max-w-3xl"
          >
            <div className="flex items-center gap-2 mb-6">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-electric/15 text-electric text-xs font-semibold border border-electric/20">
                <span className="w-2 h-2 rounded-full bg-electric animate-pulse" />
                3 200+ clients satisfaits
              </span>
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-electric/10 text-electric text-xs font-semibold border border-electric/15">
                <TrendingUp className="w-3 h-3" />
                +420 commandes ce mois
              </span>
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-7xl font-extrabold tracking-tight text-foreground leading-[1.08]">
              Le style qui{" "}
              <span className="text-gradient-electric">définit l'élégance</span>
            </h1>
            <p className="mt-6 text-lg sm:text-xl text-muted-foreground max-w-xl leading-relaxed">
              Vêtements et chaussures de marque. T-shirts, hoodies, sneakers — un style premium accessible.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link
                to="/catalog"
                className="inline-flex items-center px-8 py-4 rounded-xl bg-gradient-electric text-electric-foreground font-semibold text-base shadow-electric hover:opacity-90 transition-opacity"
              >
                Découvrir la collection
              </Link>
            </div>
            <div className="mt-8 flex flex-wrap items-center gap-6 text-muted-foreground">
              <span className="flex items-center gap-1.5 text-xs font-medium"><ShieldCheck className="w-4 h-4 text-badge-success" /> Paiement sécurisé</span>
              <span className="flex items-center gap-1.5 text-xs font-medium"><Truck className="w-4 h-4 text-electric" /> Livraison rapide</span>
              <span className="flex items-center gap-1.5 text-xs font-medium"><Award className="w-4 h-4 text-electric" /> Noté 4.9/5</span>
            </div>
          </motion.div>
        </div>
        <div className="absolute top-1/2 right-0 -translate-y-1/2 w-[500px] h-[500px] rounded-full bg-electric/5 blur-3xl pointer-events-none" />
      </section>

      {/* Stats */}
      <section className="border-b border-border bg-surface-elevated">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { value: "3 200+", label: "Clients satisfaits", icon: Users },
              { value: "4.9/5", label: "Note moyenne", icon: Star },
              { value: "18 000+", label: "Pièces vendues", icon: TrendingUp },
              { value: "98%", label: "Taux de satisfaction", icon: Award },
            ].map((stat) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="text-center"
              >
                <stat.icon className="w-5 h-5 text-electric mx-auto mb-2" />
                <div className="font-display text-2xl sm:text-3xl font-bold text-foreground">{stat.value}</div>
                <div className="text-xs text-muted-foreground mt-1">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Live activity */}
      <section className="bg-background border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-center gap-3 text-sm">
            <span className="w-2 h-2 rounded-full bg-badge-success animate-pulse" />
            <span className="text-muted-foreground">
              <strong className="text-foreground">Sarah M.</strong> vient d'acheter le <strong className="text-foreground">Hoodie Oversize</strong> il y a 4 min
            </span>
          </div>
        </div>
      </section>

      {/* Summer 2026 Promo Banner */}
      <section className="relative overflow-hidden">
        <div
          className="relative bg-cover bg-center min-h-[420px] sm:min-h-[520px] flex items-center"
          style={{ backgroundImage: `url(${summer2026Bg})` }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/30 to-transparent" />
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24 w-full">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="max-w-xl"
            >
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/15 backdrop-blur-md text-white text-xs font-semibold border border-white/30">
                <Sparkles className="w-3 h-3" />
                Édition limitée
              </span>
              <h2 className="mt-5 font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white tracking-tight leading-[1.05] drop-shadow-lg">
                Collection<br />Summer 2026
              </h2>
              <p className="mt-5 text-base sm:text-lg text-white/90 max-w-md leading-relaxed drop-shadow">
                Profitez de l'été en avance — jusqu'à <strong className="text-white">-30%</strong> sur la nouvelle collection. Code <strong className="text-white">SUMMERS</strong> pour 5% supplémentaires.
              </p>
              <div className="mt-8 flex flex-wrap gap-4">
                <Link
                  to="/catalog"
                  className="inline-flex items-center px-7 py-3.5 rounded-xl bg-white text-foreground font-semibold text-sm shadow-lg hover:bg-white/90 transition-all"
                >
                  Découvrir la collection
                </Link>
                <Link
                  to="/catalog"
                  className="inline-flex items-center px-7 py-3.5 rounded-xl bg-white/10 backdrop-blur-md text-white font-semibold text-sm border border-white/30 hover:bg-white/20 transition-all"
                >
                  Voir les promos
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Featured Products */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }}>
          <h2 className="font-display text-3xl sm:text-4xl font-bold text-foreground tracking-tight text-center">
            Marques de luxe — Bestsellers
          </h2>
          <p className="text-muted-foreground text-center mt-3 text-base">Louis Vuitton, Dior, Gucci, Golden Goose, Cartier, Goyard, Burberry</p>
        </motion.div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-12">
          {featured.map((product, i) => (
            <ProductCard key={product.id} product={product} index={i} />
          ))}
        </div>
        <div className="text-center mt-12">
          <Link to="/catalog" className="text-sm font-semibold text-electric hover:underline underline-offset-4 transition-colors">
            Voir toute la collection →
          </Link>
        </div>
      </section>

      {/* Arguments */}
      <section className="bg-surface py-20 sm:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { icon: Sparkles, title: "Qualité premium", text: "Matières sélectionnées avec soin. Coton bio, cachemire, cuir pleine fleur." },
              { icon: Truck, title: "Livraison offerte dès 59€", text: "Livraison rapide et soignée. Retours gratuits sous 1 mois." },
              { icon: Headphones, title: "Support client", text: "Une équipe disponible 7j/7 par chat, email ou téléphone." },
            ].map((item, i) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.15 }}
                className="text-center p-8"
              >
                <div className="w-14 h-14 rounded-2xl bg-electric/10 flex items-center justify-center mx-auto mb-5">
                  <item.icon className="w-6 h-6 text-electric" />
                </div>
                <h3 className="font-display font-semibold text-lg text-foreground mb-2">{item.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{item.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Reviews */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }}>
          <div className="flex items-center justify-center gap-3 mb-2">
            <div className="flex gap-0.5">
              {Array.from({ length: 5 }).map((_, j) => (
                <Star key={j} className="w-5 h-5 fill-star text-star" />
              ))}
            </div>
            <span className="text-sm font-semibold text-foreground">4.9/5</span>
            <span className="text-sm text-muted-foreground">basé sur 1 240 avis</span>
          </div>
          <h2 className="font-display text-3xl sm:text-4xl font-bold text-foreground tracking-tight text-center">
            Ce que disent nos clients
          </h2>
        </motion.div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12">
          {[
            { name: "Thomas D.", text: "Le hoodie oversize est incroyable, doux et chaud. Qualité vraiment premium pour le prix.", rating: 5, date: "il y a 2 jours", verified: true },
            { name: "Sophie M.", text: "Les sneakers sont magnifiques et ultra-confortables dès le premier jour. Je recommande !", rating: 5, date: "il y a 5 jours", verified: true },
            { name: "Marc L.", text: "Commandé le Pack Essentiel, reçu en 2 jours. Tout est parfait. Je recommande !", rating: 5, date: "il y a 1 semaine", verified: true },
            { name: "Julie P.", text: "Le t-shirt est d'une qualité incroyable. Le coton bio se sent directement. Je rachète !", rating: 5, date: "il y a 3 jours", verified: true },
            { name: "Nicolas R.", text: "Le pull cachemire est une tuerie. Doux, chaud, élégant. Parfait pour cet hiver.", rating: 5, date: "il y a 4 jours", verified: true },
            { name: "Camille B.", text: "Deuxième commande chez angle-shop.com, toujours aussi satisfaite. Livraison rapide et emballage soigné.", rating: 5, date: "il y a 6 jours", verified: true },
          ].map((review, i) => (
            <motion.div
              key={review.name}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
              className="bg-card rounded-2xl p-6 shadow-card"
            >
              <div className="flex items-center justify-between mb-3">
                <div className="flex gap-0.5">
                  {Array.from({ length: review.rating }).map((_, j) => (
                    <Star key={j} className="w-4 h-4 fill-star text-star" />
                  ))}
                </div>
                {review.verified && (
                  <span className="text-[10px] font-medium text-badge-success bg-badge-success/10 px-2 py-0.5 rounded-full flex items-center gap-1">
                    <ShieldCheck className="w-3 h-3" /> Achat vérifié
                  </span>
                )}
              </div>
              <p className="text-sm text-foreground leading-relaxed mb-4">"{review.text}"</p>
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold text-muted-foreground">{review.name}</p>
                <p className="text-[10px] text-muted-foreground">{review.date}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Trust logos */}
      <section className="bg-surface border-t border-border py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-xs text-muted-foreground uppercase tracking-widest mb-6">Ils nous font confiance</p>
          <div className="flex flex-wrap items-center justify-center gap-8">
            {["Vogue", "GQ", "Hypebeast", "Highsnobiety", "ELLE"].map((name) => (
              <span key={name} className="font-display font-bold text-lg text-muted-foreground">{name}</span>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}
